import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import moment from 'moment';
import PhoneInput, { getCountryCallingCode } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import PhoneCountrySelect from 'components/PhoneCountrySelect';
import { keepCallingCodeOnKeyDown } from 'components/phoneCountryUtils';

import PageHeader from 'components/pageHeader';
import SavingModal from 'components/SavingModal';
import useResponsive from 'components/UserDashboardV2/hooks/useResponsive';
import { COLORS } from 'components/UserDashboardV2/utils/constants';
import { sanitizeName } from 'components/UserDashboardV2/utils/formatters';
import { addStudent, enrollStudent } from 'actions/studentManagement';
import { getTeachersGeofenceLocations } from 'actions/attendanceNew';
import { getMarketSettingsData } from 'actions/studentMarketPlace';
import { Notification, Api } from 'services';

const ChevronDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M6 9L12 15L18 9" stroke="#666666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronNavIcon = ({ direction = "left" }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: direction === "right" ? "rotate(180deg)" : undefined }}>
        <path d="M15 6L9 12L15 18" stroke="#666666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CalendarTriggerIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <rect x="3" y="5" width="18" height="16" rx="3" stroke="#666666" strokeWidth="1.6" />
        <path d="M3 9.5H21" stroke="#666666" strokeWidth="1.6" />
        <path d="M8 3V6.5" stroke="#666666" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M16 3V6.5" stroke="#666666" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

const PlusIcon = ({ color = COLORS.primary }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);

const CheckIcon = ({ color = COLORS.primary }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M5 13L9 17L19 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

import VideoCourseIconSvg from './dashboardV3/video-course-icon.svg';
import StudyMaterialsIconSvg from './dashboardV3/study-materials-icon.svg';
import TestSeriesIconSvg from './dashboardV3/test-series-icon.svg';
import LiveCoursesIconSvg from './dashboardV3/live-courses-icon.svg';

const LiveCourseIcon = ({ size = 24 }) => (
    <img src={LiveCoursesIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const TestSeriesIcon = ({ size = 24 }) => (
    <img src={TestSeriesIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const VideoCourseIcon = ({ size = 24 }) => (
    <img src={VideoCourseIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const StudyMaterialsIcon = ({ size = 24 }) => (
    <img src={StudyMaterialsIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const ALL_COURSE_TYPES = [
    { key: "live", label: "Classroom course", Icon: VideoCourseIcon, bg: "#D8F0FF", color: "#008BE3" },
    { key: "video", label: "Video course", Icon: StudyMaterialsIcon, bg: "#DCF2B7", color: "#609900" },
    { key: "test", label: "Test series", Icon: LiveCourseIcon, bg: "#FFEFC5", color: "#D97706" },
    { key: "study", label: "Study materials", Icon: TestSeriesIcon, bg: "#FFDEDF", color: "#DC2626" },
];

const getAvailableCourseTypes = (marketSettings, initialNavType) => {
    if (!marketSettings || Object.keys(marketSettings).length === 0) {
        if (initialNavType && initialNavType !== 'live') {
            const navItem = ALL_COURSE_TYPES.find(t => t.key === initialNavType);
            return navItem ? [ALL_COURSE_TYPES[0], navItem] : [ALL_COURSE_TYPES[0]];
        }
        return [ALL_COURSE_TYPES[0]];
    }
    const settings = marketSettings?.data ?? marketSettings;
    const types = [];
    if (settings?.liveCourseSelected !== false) {
        types.push(ALL_COURSE_TYPES[0]);
    }
    if (settings?.videoResourceSelected) {
        types.push(ALL_COURSE_TYPES[1]);
    }
    if (settings?.onlineTestSelected) {
        types.push(ALL_COURSE_TYPES[2]);
    }
    if (settings?.studyMaterialSelected) {
        types.push(ALL_COURSE_TYPES[3]);
    }
    return types.length > 0 ? types : [ALL_COURSE_TYPES[0]];
};

// Dynamic option states will replace these static lists
// COURSE_OPTIONS and BATCH_OPTIONS removed

// Same validation as addStaffDrawer.js's Add staff form.
const mobileRegex = /^[6-9]\d{9}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const fieldLabelStyle = {
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#636363",
    marginBottom: "8px",
    display: "block",
};

const requiredMarkStyle = { color: "#FF0000", marginLeft: "2px" };

const inputBoxStyle = (hasError) => ({
    width: "100%",
    height: "44px",
    padding: "0 14px",
    boxSizing: "border-box",
    borderRadius: "12px",
    border: hasError ? "1px solid #FF414D" : "none",
    backgroundColor: "#F6F6F6",
    fontSize: "15px",
    color: "#191919",
    outline: "none",
});

const errorTextStyle = {
    fontFamily: "Roboto, sans-serif",
    fontSize: "12px",
    color: "#FF414D",
    marginTop: "6px",
};

const popoverPanelStyle = {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
    padding: "8px",
    zIndex: 30,
    maxHeight: "220px",
    overflowY: "auto",
    boxSizing: "border-box",
};

const optionRowStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
};

const navButtonStyle = {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    border: "none",
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
};

const cancelButtonStyle = {
    height: "34px",
    padding: "0 22px",
    borderRadius: "10px",
    border: `2px solid ${COLORS.primary}`,
    backgroundColor: "transparent",
    color: COLORS.primary,
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
};

const saveButtonStyle = {
    ...cancelButtonStyle,
    border: "none",
    backgroundColor: COLORS.primary,
    color: "#FFFFFF",
};

// Nearest ancestor that clips by scrolling, falling back to the viewport —
// the area a dropdown/calendar popover has to fit inside.
const getVisibleBounds = (el) => {
    let node = el ? el.parentElement : null;
    while (node && node !== document.body) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') {
            const rect = node.getBoundingClientRect();
            return { top: Math.max(rect.top, 0), bottom: Math.min(rect.bottom, window.innerHeight) };
        }
        node = node.parentElement;
    }
    return { top: 0, bottom: window.innerHeight };
};

// Runs before paint once a popover opens: measures it and returns true when
// it would be cut off below (would need a scroll to see) but has more room
// above — so it opens upward instead. Decided only on open.
const useOpenAbove = (isOpen, anchorRef, panelRef) => {
    const [openAbove, setOpenAbove] = useState(false);
    useLayoutEffect(() => {
        if (!isOpen || !anchorRef.current || !panelRef.current) {
            setOpenAbove(false);
            return;
        }
        const gap = 8;
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const panelHeight = panelRef.current.offsetHeight + gap;
        const bounds = getVisibleBounds(anchorRef.current);
        const spaceBelow = bounds.bottom - anchorRect.bottom;
        const spaceAbove = anchorRect.top - bounds.top;
        setOpenAbove(spaceBelow < panelHeight && spaceAbove > spaceBelow);
    }, [isOpen]);
    return openAbove;
};

const TextField = ({ label, required, placeholder, value, onChange, error, errorMessage = "This field is required.", maxLength, disabled = false }) => (
    <div style={{ flex: "1 1 220px", minWidth: 0 }}>
        <label style={fieldLabelStyle}>{label}{required && <span style={requiredMarkStyle}>*</span>}</label>
        <input
            type="text"
            value={value}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className="add-student-textfield"
            style={{ ...inputBoxStyle(error), ...(disabled ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
        />
        {error && <div style={errorTextStyle}>{errorMessage}</div>}
    </div>
);

// Every dropdown shows a right chevron while closed and a down chevron while
// open. `searchable` (Course / Batch in Course enrollment) additionally turns
// the box into a text input while open that filters the options by label.
const Dropdown = ({ label, required, placeholder, value, options = [], defaultLabel = '', error, popoverKey, openKey, onOpen, onSelect, onClear, getLabel = o => o, getValue = o => o, disabled = false, searchable = false, loading = false, emptyText = "No options available" }) => {
    const isOpen = !disabled && openKey === popoverKey;
    const anchorRef = useRef(null);
    const panelRef = useRef(null);
    const openAbove = useOpenAbove(isOpen, anchorRef, panelRef);
    const [searchText, setSearchText] = useState("");
    const searchInputRef = useRef(null);
    // Text the search box was pre-filled with on open (the selected value) —
    // while it's unchanged, all options are shown, not just that one.
    const openedWithRef = useRef("");
    const matchedOpt = options.find(o => String(getValue(o)) === String(value));
    const displayValue = value ? (matchedOpt ? getLabel(matchedOpt) : (defaultLabel || (typeof value === 'string' ? value : "Global"))) : (defaultLabel || "");
    const hasSelection = Boolean(value || defaultLabel);

    // On open: put the selected value into the search box as real text (not
    // a placeholder) with the cursor after its last character. On close:
    // clear it.
    useLayoutEffect(() => {
        if (!searchable) return;
        if (isOpen) {
            const initial = hasSelection ? String(displayValue ?? "") : "";
            openedWithRef.current = initial;
            setSearchText(initial);
            requestAnimationFrame(() => {
                const el = searchInputRef.current;
                if (el) {
                    el.focus();
                    el.setSelectionRange(initial.length, initial.length);
                }
            });
        } else {
            openedWithRef.current = "";
            setSearchText("");
        }
    }, [isOpen]); // displayValue read only at open time

    const query = searchText.trim().toLowerCase();
    const isUnchangedFromOpen = searchText === openedWithRef.current;
    const visibleOptions = searchable && query && !isUnchangedFromOpen
        ? options.filter((o) => String(getLabel(o) ?? "").toLowerCase().includes(query))
        : options;

    // On open, scroll the list so the selected option is in view (centered).
    // Only the panel's own scrollTop changes — the page itself doesn't scroll.
    useLayoutEffect(() => {
        if (!isOpen) return;
        const panel = panelRef.current;
        const selected = panel?.querySelector('[data-selected="true"]');
        if (!panel || !selected) return;
        panel.scrollTop = selected.offsetTop - (panel.clientHeight - selected.offsetHeight) / 2;
    }, [isOpen]); // selected row read only at open time

    return (
        <div data-av2-popover style={{ position: "relative", flex: "1 1 220px", minWidth: 0 }}>
            <label style={fieldLabelStyle}>{label}{required && <span style={requiredMarkStyle}>*</span>}</label>
            <div
                ref={anchorRef}
                onClick={() => { if (!disabled) onOpen(isOpen ? null : popoverKey); }}
                style={{
                    ...inputBoxStyle(error),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                {searchable && isOpen ? (
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            // Text fully cleared -> deselect (list stays open).
                            if (!e.target.value && value) onClear?.();
                        }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder={placeholder}
                        className="add-student-dropdown-search"
                        style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontSize: "15px", color: "#191919", padding: 0 }}
                    />
                ) : (
                    <span style={{ color: (value || defaultLabel) ? "#191919" : "#B8B8B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {(value || defaultLabel) ? displayValue : placeholder}
                    </span>
                )}
                {/* Right (>) while closed, down while open — all dropdowns. */}
                <span style={{ display: "flex", transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform 0.2s ease" }}>
                    <ChevronDownIcon />
                </span>
            </div>
            {error && <div style={errorTextStyle}>This field is required.</div>}
            {isOpen && (
                <div
                    ref={panelRef}
                    style={openAbove
                        // Measured from the input box (not the wrapper, which
                        // includes the label) so the gap matches the 6px below.
                        ? { ...popoverPanelStyle, top: "auto", bottom: `calc(100% - ${anchorRef.current?.offsetTop || 0}px + 6px)` }
                        : popoverPanelStyle}
                >
                    {visibleOptions.length === 0 ? (
                        <div style={{ padding: "10px 12px", fontFamily: "Roboto, sans-serif", fontSize: "14px", color: "#8A8A8A" }}>
                            {loading ? "Loading..." : emptyText}
                        </div>
                    ) : visibleOptions.map((opt, i) => {
                        const optVal = getValue(opt);
                        const optLabel = getLabel(opt);
                        const isSelected = String(optVal) === String(value);
                        return (
                            <div
                                key={optVal || i}
                                onClick={() => onSelect(optVal)}
                                className="add-student-option-row"
                                data-selected={isSelected}
                                style={{
                                    ...optionRowStyle,
                                    color: isSelected ? COLORS.primary : "#191919",
                                    fontWeight: isSelected ? 600 : 400,
                                }}
                            >
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{optLabel}</span>
                                {isSelected && <CheckIcon />}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const DateField = ({ value, popoverKey, openKey, onOpen, onSelect }) => {
    const isOpen = openKey === popoverKey;
    const [calendarMonth, setCalendarMonth] = useState(() => (value ? moment(value) : moment()));
    const anchorRef = useRef(null);
    const panelRef = useRef(null);
    const openAbove = useOpenAbove(isOpen, anchorRef, panelRef);

    // Every time the calendar opens, start from the selected date's month
    // (today by default) — not whatever month it was left on last time.
    // Layout effect so the old month never flashes for a frame.
    useLayoutEffect(() => {
        if (isOpen) {
            setCalendarMonth(value ? moment(value) : moment());
        }
    }, [isOpen]); // value read only at open time

    const cells = useMemo(() => {
        const daysInMonth = calendarMonth.daysInMonth();
        const startOfWeek = calendarMonth.clone().startOf("month").day();
        const c = [];
        for (let i = 0; i < startOfWeek; i++) c.push(null);
        for (let i = 1; i <= daysInMonth; i++) c.push(calendarMonth.clone().date(i));
        return c;
    }, [calendarMonth]);

    return (
        <div data-av2-popover style={{ position: "relative", flex: "1 1 220px", minWidth: 0 }}>
            <label style={fieldLabelStyle}>Enrollment date</label>
            <div
                ref={anchorRef}
                onClick={() => onOpen(isOpen ? null : popoverKey)}
                style={{ ...inputBoxStyle(false), display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
            >
                <span style={{ color: value ? "#191919" : "#B8B8B8" }}>
                    {value ? moment(value).format("DD/MM/YYYY") : "dd/mm/yyyy"}
                </span>
                <CalendarTriggerIcon />
            </div>
            {isOpen && (
                <div ref={panelRef} style={{
                    position: "absolute",
                    // Above: measured from the input box (not the wrapper, which
                    // includes the label) so the gap matches the 8px below.
                    ...(openAbove
                        ? { bottom: `calc(100% - ${anchorRef.current?.offsetTop || 0}px + 8px)` }
                        : { top: "calc(100% + 8px)" }),
                    right: 0,
                    width: "340px",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "20px",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                    padding: "24px",
                    zIndex: 30,
                    boxSizing: "border-box",
                }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                        <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 600, fontSize: "17px", color: "#191919" }}>
                            {calendarMonth.format("MMMM, YYYY")}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                            <button type="button" onClick={() => setCalendarMonth((m) => m.clone().subtract(1, "month"))} style={navButtonStyle}>
                                <ChevronNavIcon direction="left" />
                            </button>
                            <button type="button" onClick={() => setCalendarMonth((m) => m.clone().add(1, "month"))} style={navButtonStyle}>
                                <ChevronNavIcon direction="right" />
                            </button>
                        </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: "10px" }}>
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                            <span key={d} style={{ textAlign: "center", fontSize: "13px", color: "#8A8A8A", fontFamily: "Roboto, sans-serif", padding: "4px 0" }}>{d}</span>
                        ))}
                        {cells.map((cell, i) => {
                            const isSelected = cell && value && cell.isSame(moment(value), "day");
                            const isToday = cell && cell.isSame(moment(), "day");
                            return (
                                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <div
                                        onClick={() => cell && onSelect(cell.format("YYYY-MM-DD"))}
                                        style={{
                                            width: "36px",
                                            height: "36px",
                                            boxSizing: "border-box",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "50%",
                                            cursor: cell ? "pointer" : "default",
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: "15px",
                                            fontWeight: isToday && !isSelected ? 600 : 400,
                                            color: isSelected ? "#FFFFFF" : isToday ? COLORS.primary : "#191919",
                                            backgroundColor: isSelected ? COLORS.primary : "transparent",
                                            border: isToday && !isSelected ? `1.5px solid ${COLORS.primary}` : "none",
                                        }}
                                    >
                                        {cell ? cell.date() : ""}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const SectionCard = ({ title, children, isMobile }) => (
    <div style={{
        backgroundColor: "#FFFFFF",
        borderRadius: "24px",
        padding: isMobile ? "18px" : "24px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        boxSizing: "border-box",
    }}>
        {title && <span style={{ fontWeight: 500, fontSize: "20px", color: "#191919" }}>{title}</span>}
        {children}
    </div>
);

// No course type is pre-selected — enrollment is optional. An entry with no
// type counts as "no enrollment"; its fields only appear once a type card is
// selected (clicking the selected card again deselects it).
const emptyCourseEntry = () => ({
    type: null,
    course: "",
    batch: "",
    rollNumber: "",
    enrollmentDate: moment().format('YYYY-MM-DD'),
});

// "+<calling code>" for a country (e.g. "IN" -> "+91"), "" if unknown.
const callingPrefixOf = (country) => {
    if (!country) return "";
    try {
        return `+${getCountryCallingCode(country)}`;
    } catch (e) {
        return "";
    }
};

// Stop waiting for the roll-number API after this long — the field and SAVE
// are re-enabled so the user can type a roll number themselves.
const ROLL_FETCH_TIMEOUT_MS = 5000;

// Roll numbers already taken in a batch, from /userv2/student/roll-number's
// `existingRollNumbers` (e.g. ["10","11","12"]).
const existingRollsOf = (res) => {
    const list = res?.existingRollNumbers ?? res?.data?.existingRollNumbers;
    return Array.isArray(list) ? list.map((r) => String(r).trim()) : [];
};

// "012" and "12" are the same roll number.
const normalizeRoll = (roll) => {
    const r = String(roll ?? "").trim();
    return /^\d+$/.test(r) ? String(Number(r)) : r.toLowerCase();
};

// A typed roll number that is already assigned in the selected batch.
const isRollNumberTaken = (entry) => {
    if (entry.type !== 'live') return false;
    const roll = normalizeRoll(entry.rollNumber);
    if (!roll) return false;
    return (entry.existingRollNumbers || []).some((r) => normalizeRoll(r) === roll);
};

const mapCourseTypeToKey = (type) => {
    if (!type) return "live";
    const lower = String(type).toLowerCase();
    if (lower.includes("video")) return "video";
    if (lower.includes("test")) return "test";
    if (lower.includes("study")) return "study";
    return "live";
};

const AddStudent = ({ addStudent, enrollStudent, getTeachersGeofenceLocations, teachersGeofenceLocDataContainer, marketSettingData, getMarketSettingsData }) => {
    const history = useHistory();
    const location = useLocation();
    const { isMobile } = useResponsive();

    const [name, setName] = useState("");
    const [mobile, setMobile] = useState("");
    // Keeps the typed number when the phone country is switched — in
    // international mode the library resets the field to just the new "+code".
    // mobileRef mirrors the latest value; pendingCountrySwitchRef holds the
    // digits to re-attach to the new code.
    const [phoneCountry, setPhoneCountry] = useState("IN");
    const mobileRef = useRef("");
    const pendingCountrySwitchRef = useRef(null);
    const [email, setEmail] = useState("");
    const [geofenceId, setGeofenceId] = useState(1);
    const [geofence, setGeofence] = useState("Global");
    const [locationOptions, setLocationOptions] = useState([]);
    const [courseEntries, setCourseEntries] = useState(() => {
        const stateCourseId = location.state?.courseId;
        const stateCourseName = location.state?.courseName;
        if (stateCourseId || stateCourseName) {
            const mappedType = mapCourseTypeToKey(location.state?.courseType || location.state?.resourceType);
            const standardId = location.state?.standardId;
            return [{
                type: mappedType,
                course: stateCourseId ? (Number(stateCourseId) || stateCourseId) : "",
                batch: standardId ? (Number(standardId) || standardId) : "",
                rollNumber: "",
                enrollmentDate: moment().format('YYYY-MM-DD'),
            }];
        }
        return [emptyCourseEntry()];
    });
    const [errors, setErrors] = useState({});
    const [openPopover, setOpenPopover] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [saving, setSaving] = useState(false);

    const [courseOptionsMap, setCourseOptionsMap] = useState({});
    const [batchOptionsMap, setBatchOptionsMap] = useState({});

    // Roll-number fetches are tagged on the entry itself (rollRequestId), so a
    // late reply lands on the right block even if blocks were added/removed,
    // and is ignored if that block's course/batch changed since.
    const rollRequestCounterRef = useRef(0);

    const fetchRollNumber = (requestId, standardId) => {
        // Stop blocking the field / SAVE if the API is slow; a late reply
        // still fills in the roll number (unless the user typed one).
        setTimeout(() => {
            setCourseEntries((current) => current.map((e) => (
                e.rollRequestId === requestId && e.rollLoading ? { ...e, rollLoading: false } : e
            )));
        }, ROLL_FETCH_TIMEOUT_MS);
        // timed=true: send now, not after Api's 1s debounce.
        Api.get('/userv2/student/roll-number').params({ standardId }).send((res) => {
            const autoRoll = res ? (res.nextRollNo ?? res.data?.nextRollNo ?? res.rollNo ?? res.rollNumber ?? res.data?.rollNo ?? res.data?.rollNumber ?? res.data ?? res) : null;
            setCourseEntries((current) => current.map((e) => {
                if (e.rollRequestId !== requestId) return e;
                return {
                    ...e,
                    rollLoading: false,
                    rollRequestId: null,
                    ...(res ? { existingRollNumbers: existingRollsOf(res) } : {}),
                    ...(res && !e.rollEdited && autoRoll && typeof autoRoll !== 'object' ? { rollNumber: String(autoRoll) } : {}),
                };
            }));
        }, true);
    };

    // Auto-fetch roll number if standardId was provided in navigation state
    useEffect(() => {
        const standardId = location.state?.standardId;
        if (standardId) {
            const requestId = ++rollRequestCounterRef.current;
            setCourseEntries((currentEntries) => currentEntries.map((e, idx) => (idx === 0 ? {
                ...e,
                rollLoading: true,
                rollRequestId: requestId,
                rollEdited: false,
            } : e)));
            fetchRollNumber(requestId, standardId);
        }
    }, [location.state?.standardId]);

    // Live (not just on-Save) validation — mobile comes from PhoneInput as a
    // full E.164 value that grows as you type (e.g. typing "98788999" for
    // India becomes "+9198788999"). Stripping the "+91"/"91" country-code
    // prefix off the FRONT (not slice(-10) off the end) matters here:
    // while the national number is still under 10 digits, the total digit
    // count can coincidentally equal 10 once the "91" is included (e.g. "91"
    // + 8 digits = 10), so taking the last 10 digits grabs part of the
    // country code as if it were the number and can pass by accident.
    // Stripping the known prefix instead always leaves the real national
    // number, whatever its current length, so the regex (which requires
    // exactly 10 digits) correctly stays failing until it actually is 10.
    const mobileDigits = mobile.startsWith('+91') ? mobile.slice(3)
        : mobile.startsWith('91') ? mobile.slice(2)
            : mobile.replace(/\D/g, "");
    // Digits typed after the selected country's code — the error only shows
    // once at least one has been entered (not for a bare "+354").
    const phoneCountryPrefix = callingPrefixOf(phoneCountry);
    const typedMobileDigits = (phoneCountryPrefix && mobile.startsWith(phoneCountryPrefix)
        ? mobile.slice(phoneCountryPrefix.length)
        : mobile.replace(/^\+/, "")).replace(/\D/g, "");
    // Only Indian (+91) numbers are validated (10-digit mobile regex, as
    // before); for any other country a number just needs to be entered.
    const isIndianMobile = mobile.startsWith('+91') || (!mobile.startsWith('+') && phoneCountry === 'IN');
    const isMobileValid = isIndianMobile
        ? mobileRegex.test(mobileDigits)
        : typedMobileDigits.length > 0;
    const mobileError = typedMobileDigits.length > 0 && !isMobileValid;
    const trimmedEmail = email.trim();
    const isEmailValid = !trimmedEmail || emailRegex.test(trimmedEmail);
    const emailError = trimmedEmail.length > 0 && !isEmailValid;
    // Once a course type card is selected, that enrollment must be completed
    // (course, plus batch for live) before saving — same rules as the Save
    // validation below. Deselecting the card removes the requirement.
    const areEnrollmentsComplete = courseEntries.every((entry) => {
        if (!entry.type) return true;
        if (!entry.course) return false;
        if (entry.type === 'live' && !entry.batch) return false;
        // Roll number already assigned in the selected batch.
        if (isRollNumberTaken(entry)) return false;
        // Still fetching the next roll number for the selected batch.
        if (entry.rollLoading) return false;
        return true;
    });
    const canSave = Boolean(name.trim()) && isMobileValid && isEmailValid && areEnrollmentsComplete;

    const containerRef = useRef(null);

    const setMobileValue = (val) => {
        mobileRef.current = val;
        setMobile(val);
    };

    const handleMobileChange = (val) => {
        let next = val || "";
        // The library's onChange right after a country switch carries only the
        // new bare "+code" — put the previously typed digits back after it.
        const pending = pendingCountrySwitchRef.current;
        if (pending) {
            pendingCountrySwitchRef.current = null;
            if (next === pending.prefix) next = `${pending.prefix}${pending.digits}`;
        }
        setMobileValue(next);
    };

    // React calls this (from the library's componentDidUpdate) BEFORE the
    // library's onChange with the new "+code", so mobileRef still holds the
    // full number typed for the old country here.
    const handlePhoneCountryChange = (newCountry) => {
        const oldCountry = phoneCountry;
        setPhoneCountry(newCountry);
        if (!newCountry || !oldCountry || newCountry === oldCountry) return;
        const oldPrefix = callingPrefixOf(oldCountry);
        const newPrefix = callingPrefixOf(newCountry);
        if (!oldPrefix || !newPrefix) return;
        const current = mobileRef.current || "";
        const digits = current.startsWith(oldPrefix) ? current.slice(oldPrefix.length).replace(/\D/g, "") : "";
        if (!digits) return;
        if (current === newPrefix) {
            // onChange already arrived first — restore directly.
            setMobileValue(`${newPrefix}${digits}`);
        } else {
            pendingCountrySwitchRef.current = { prefix: newPrefix, digits };
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest("[data-av2-popover]")) {
                setOpenPopover(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        getTeachersGeofenceLocations((res, err) => {
            if (res) {
                const list = Array.isArray(res)
                    ? res
                    : (Array.isArray(res?.data) ? res.data : (Array.isArray(res?.result) ? res.result : []));
                if (list && list.length > 0) {
                    setLocationOptions(list);
                    const globalLoc = list.find(item => (item?.title || item?.geoFenceTitle || "").trim().toLowerCase() === "global");
                    if (globalLoc) {
                        setGeofenceId(globalLoc.id ?? globalLoc.geoFenceId);
                        setGeofence(globalLoc.title || "Global");
                    }
                }
            }
        });
    }, [getTeachersGeofenceLocations]);

    useEffect(() => {
        if (Array.isArray(teachersGeofenceLocDataContainer) && teachersGeofenceLocDataContainer.length > 0) {
            setLocationOptions(teachersGeofenceLocDataContainer);
            const globalLoc = teachersGeofenceLocDataContainer.find(item => (item?.title || item?.geoFenceTitle || "").trim().toLowerCase() === "global");
            if (globalLoc) {
                setGeofenceId(prev => prev ?? (globalLoc.id ?? globalLoc.geoFenceId));
                setGeofence(prev => prev || (globalLoc.title || "Global"));
            }
        }
    }, [teachersGeofenceLocDataContainer]);

    // Course lists are cached per course type and fetched once, immediately
    // (timed=true skips Api's 1s debounce), so the Course dropdown has its
    // options as soon as a type card is selected. Batches are cached per course.
    const [coursesByType, setCoursesByType] = useState({});
    const [batchesByCourse, setBatchesByCourse] = useState({});
    const coursesInFlightRef = useRef({});
    const batchesInFlightRef = useRef({});

    const fetchCoursesForType = (type) => {
        if (!type || coursesInFlightRef.current[type]) return;
        let endpoint = "";
        if (type === 'live') endpoint = "/course/filter-course";
        else if (type === 'video') endpoint = "/course/filter-video-resource";
        else if (type === 'test') endpoint = "/testSeries/filter-test-series";
        else if (type === 'study') endpoint = "/course/filter-study-material";
        if (!endpoint) return;
        coursesInFlightRef.current[type] = true;
        Api.get(endpoint).params({ disablePagination: true }).send((res) => {
            const raw = res?.data ?? res;
            let items = [];
            if (Array.isArray(raw)) {
                items = raw;
            } else if (raw && typeof raw === 'object') {
                items = raw.videoResources?.items || (Array.isArray(raw.videoResources) ? raw.videoResources : null) ||
                    raw.courses?.items || (Array.isArray(raw.courses) ? raw.courses : null) ||
                    raw.testSeries?.items || (Array.isArray(raw.testSeries) ? raw.testSeries : null) ||
                    raw.studyMaterials?.items || (Array.isArray(raw.studyMaterials) ? raw.studyMaterials : null) ||
                    raw.items || (Array.isArray(raw.data) ? raw.data : []);
            }
            let list = Array.isArray(items) ? items : [];
            // Classroom courses: show only ones that have batches AND chapters/subjects
            // (counts come as strings, e.g. "0"). A missing count is treated as "has some".
            if (type === 'live') {
                const has = (v) => v === undefined || v === null || Number(v) > 0;
                list = list.filter((c) => has(c?.batchCount) && (has(c?.chapterCount) || has(c?.subjectCount)));
            }
            setCoursesByType(prev => ({ ...prev, [type]: list }));
        }, true);
    };

    const fetchBatchesForCourse = (courseId) => {
        const key = String(courseId);
        if (!courseId || batchesInFlightRef.current[key]) return;
        batchesInFlightRef.current[key] = true;
        Api.get("/standards").params({ courseId }).send((res) => {
            let batches = [];
            if (Array.isArray(res)) {
                batches = res;
            } else if (res?.data) {
                if (Array.isArray(res.data)) {
                    batches = res.data;
                } else if (typeof res.data === 'object') {
                    batches = Object.values(res.data).flat();
                }
            }
            setBatchesByCourse(prev => ({ ...prev, [key]: batches }));
        }, true);
    };

    useEffect(() => {
        getMarketSettingsData?.(true);
    }, [getMarketSettingsData]);

    const initialNavType = useMemo(() => {
        return mapCourseTypeToKey(location.state?.courseType || location.state?.resourceType);
    }, [location.state]);

    const availableCourseTypes = useMemo(() => {
        return getAvailableCourseTypes(marketSettingData, initialNavType);
    }, [marketSettingData, initialNavType]);

    // If marketSettingData loaded and an entry has a type not enabled for the org, reset its type
    useEffect(() => {
        if (marketSettingData && Object.keys(marketSettingData).length > 0 && availableCourseTypes.length > 0) {
            setCourseEntries((prev) => prev.map((entry) => {
                if (entry.type && !availableCourseTypes.some((t) => t.key === entry.type)) {
                    return { ...entry, type: null, course: "", batch: "", rollNumber: "" };
                }
                return entry;
            }));
        }
    }, [marketSettingData, availableCourseTypes]);

    // Prefetch course lists when the page opens for enabled course types.
    useEffect(() => {
        availableCourseTypes.forEach(({ key }) => fetchCoursesForType(key));
    }, [availableCourseTypes]);

    // Fill each entry's options from the caches (fetching anything missing).
    useEffect(() => {
        courseEntries.forEach((entry, index) => {
            if (entry.type && !courseOptionsMap[index]) {
                if (coursesByType[entry.type]) {
                    setCourseOptionsMap(prev => ({ ...prev, [index]: coursesByType[entry.type] }));
                } else {
                    fetchCoursesForType(entry.type);
                }
            }
            if (entry.course && entry.type === 'live' && !batchOptionsMap[index]) {
                const key = String(entry.course);
                if (batchesByCourse[key]) {
                    setBatchOptionsMap(prev => ({ ...prev, [index]: batchesByCourse[key] }));
                } else {
                    fetchBatchesForCourse(entry.course);
                }
            }
        });
    }, [courseEntries, courseOptionsMap, batchOptionsMap, coursesByType, batchesByCourse]);

    const updateEntry = (index, key, val, { keepOpen = false } = {}) => {
        const rollRequestId = (key === 'batch' && val) ? ++rollRequestCounterRef.current : null;
        setCourseEntries((prev) => prev.map((entry, i) => {
            if (i === index) {
                const newEntry = { ...entry, [key]: val };
                if (key === 'type' || key === 'course') {
                    // Drop any roll-number fetch in flight for the old selection.
                    newEntry.rollLoading = false;
                    newEntry.rollRequestId = null;
                }
                if (key === 'type') {
                    newEntry.course = "";
                    newEntry.batch = "";
                    newEntry.rollNumber = "";
                    newEntry.existingRollNumbers = [];
                    setCourseOptionsMap(prevMap => ({ ...prevMap, [index]: null }));
                    setBatchOptionsMap(prevMap => ({ ...prevMap, [index]: null }));
                } else if (key === 'course') {
                    newEntry.batch = "";
                    newEntry.rollNumber = "";
                    newEntry.existingRollNumbers = [];
                    setBatchOptionsMap(prevMap => ({ ...prevMap, [index]: null }));
                } else if (key === 'batch' && val) {
                    newEntry.existingRollNumbers = [];
                    // Roll number field + SAVE wait for the next roll number.
                    newEntry.rollLoading = true;
                    newEntry.rollRequestId = rollRequestId;
                    newEntry.rollEdited = false;
                } else if (key === 'rollNumber') {
                    // Typed by the user — a late auto-fill must not overwrite it.
                    newEntry.rollEdited = true;
                }
                return newEntry;
            }
            return entry;
        }));
        if (rollRequestId) fetchRollNumber(rollRequestId, val);
        if (!keepOpen) setOpenPopover(null);
    };

    // Each enrollment block's wrapper — used to bring a newly added one into view.
    const entryRefs = useRef([]);
    const scrollToNewEntryRef = useRef(false);

    const addCourseEntry = () => {
        scrollToNewEntryRef.current = true;
        setCourseEntries((prev) => [...prev, emptyCourseEntry()]);
    };

    // After ADD ANOTHER COURSE: scroll the page's own scroll area (not the app
    // layout) so the new "Course N enrollment" heading sits near the top.
    useEffect(() => {
        if (!scrollToNewEntryRef.current) return;
        scrollToNewEntryRef.current = false;
        const el = entryRefs.current[courseEntries.length - 1];
        if (!el) return;
        let parent = el.parentElement;
        while (parent && parent !== document.body) {
            const { overflowY } = window.getComputedStyle(parent);
            if (overflowY === 'auto' || overflowY === 'scroll') break;
            parent = parent.parentElement;
        }
        if (!parent || parent === document.body) return;
        const delta = el.getBoundingClientRect().top - parent.getBoundingClientRect().top - 16;
        parent.scrollBy({ top: delta, behavior: 'smooth' });
    }, [courseEntries.length]);

    const removeCourseEntry = (index) => {
        setCourseEntries((prev) => prev.filter((_, i) => i !== index));
    };

    // This page is only ever reached from the Students tab on
    // /user-management — going back should land there with that tab still
    // active, not the "STAFF" default useState("STAFF") on that page would
    // otherwise fall back to since navigating here unmounted it.
    const goBackToStudentsTab = () => {
        if (location.state?.fromBatch || location.pathname.includes('/student-list') || window.history.length > 1) {
            history.goBack();
        } else {
            const basePath = location.pathname.startsWith('/user-management-v2') ? '/user-management-v2' : '/user-management';
            history.replace({ pathname: `${basePath}/students`, state: { activeTab: "STUDENTS" } });
        }
    };

    const handleCancel = () => {
        goBackToStudentsTab();
    };

    const handleSave = () => {
        if (!canSave) return;

        // Validation for course entries (batch only required for live course)
        const entryErrors = courseEntries.map((entry) => ({
            // An entry with no type selected is no enrollment.
            course: entry.type ? !entry.course : false,
            batch: entry.type === 'live' && entry.course ? !entry.batch : false,
        }));
        if (entryErrors.some((e) => e.course || e.batch)) {
            setErrors({ entries: entryErrors });
            return;
        }

        setShowConfirm(true);
    };

    // The confirm modal stays open showing "SAVING..." (via SavingModal's
    // loading prop) until the add + enroll calls finish; it only closes on
    // failure — on success the page navigates back.
    const handleConfirmSave = () => {
        if (saving) return;
        setSaving(true);

        const params = { name: name.trim(), phone: mobile.trim() };
        if (email.trim()) params.email = email.trim();
        if (geofenceId) params.geoFenceId = geofenceId;
        if (geofence) params.geofence = geofence;

        addStudent(params, (response, error) => {
            const userId = response?.data?.id || response?.id;
            if (response && !error && userId) {
                const enrollments = courseEntries
                    .filter(e => e.type && e.course)
                    .map(e => {
                        const isLive = !e.type || e.type === 'live';
                        return {
                            courseType: e.type === 'live' ? 'course' :
                                e.type === 'video' ? 'videoResource' :
                                    e.type === 'test' ? 'testSeries' :
                                        e.type === 'study' ? 'studyMaterial' : e.type,
                            courseId: e.course,
                            standardId: isLive ? (e.batch || undefined) : undefined,
                            rollNo: isLive ? (e.rollNumber || undefined) : undefined,
                            enrollmentDate: e.enrollmentDate ? moment(e.enrollmentDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")
                        };
                    });

                if (enrollments.length > 0) {
                    enrollStudent(userId, { enrollments }, (enrollRes, enrollErr) => {
                        setSaving(false);
                        setShowConfirm(false);
                        if (enrollRes && !enrollErr) {
                            goBackToStudentsTab();
                        } else {
                            Notification.error("Error", enrollErr?.show?.message || enrollErr?.message || "Failed to enroll student in courses");
                        }
                    });
                } else {
                    setSaving(false);
                    setShowConfirm(false);
                    goBackToStudentsTab();
                }
            } else {
                setSaving(false);
                setShowConfirm(false);
                Notification.error("Error", error?.show?.message || error?.message || "Failed to add student");
            }
        });
    };

    const bodyStyle = {
        height: isMobile ? "auto" : "100%",
        width: "calc(100% + 30px)",
        margin: -20,
        padding: isMobile ? 12 : 20,
        overflow: "visible",
        marginTop: 20,
        display: "flex",
        flexDirection: "column",
    };

    // Matches how UserDashboardV2.js measures its own #pageHeader to size
    // the scrollable body below it exactly.
    const pageHeaderEl = document.getElementById("pageHeader");
    const pageHeaderHeight = pageHeaderEl?.clientHeight || 60;

    const actions = [
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <button key="cancel" type="button" onClick={handleCancel} disabled={saving} style={cancelButtonStyle}>CANCEL</button>
            <button key="save" type="button" onClick={handleSave} disabled={saving || !canSave} style={{ ...saveButtonStyle, opacity: (saving || !canSave) ? 0.6 : 1, cursor: (saving || !canSave) ? "not-allowed" : "pointer" }}>
                SAVE
            </button>
        </div>
    ];

    return (
        <div className="dashboard-v2-page" style={bodyStyle} ref={containerRef}>
            <div id="pageHeader">
                <PageHeader
                    title="Add student"
                    onBackPress={handleCancel}
                    actions={actions}
                    actionsStyle={isMobile ? undefined : { flex: "0 0 auto", width: "auto", marginRight: "12px" }}
                />
            </div>

            <div className="dv2-outer-scroll" style={{
                height: isMobile ? "auto" : `calc(100% - ${pageHeaderHeight}px)`,
                overflowY: isMobile ? "visible" : "auto",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                width: "100%",
                marginTop: isMobile ? "12px" : "4px",
                flex: 1,
                minHeight: 0,
            }}>
                <SectionCard title="Personal information" isMobile={isMobile}>
                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                        <TextField label="Name" required placeholder="Enter name" value={name} onChange={(val) => setName(sanitizeName(val))} />
                        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                            <label style={fieldLabelStyle}>Mobile number<span style={requiredMarkStyle}>*</span></label>
                            <PhoneInput
                                className={`add-student-phone-wrapper${mobileError ? " error" : ""}`}
                                defaultCountry="IN"
                                // +91 shown by default and locked — clearing the
                                // field only removes the number; the country code
                                // changes via the flag dropdown (same as
                                // addStaffDrawer.js).
                                international
                                countryCallingCodeEditable={false}
                                placeholder="Enter mobile number"
                                value={mobile || "+91"}
                                onChange={handleMobileChange}
                                // Keep the entered number when the country changes.
                                onCountryChange={handlePhoneCountryChange}
                                addInternationalOption={true}
                                // Compact, searchable country list instead of the
                                // browser's full-height native <select>.
                                countrySelectComponent={PhoneCountrySelect}
                                // Backspace/Delete next to the locked "+91" must not wipe the number.
                                numberInputProps={{ onKeyDown: keepCallingCodeOnKeyDown }}
                                // Once it's a 10-digit Indian number (+91XXXXXXXXXX,
                                // 13 chars total), cap further typing at 11 so it
                                // can't grow past that — same pattern as
                                // addStaffDrawer.js's Mobile number field.
                                maxLength={(mobile?.startsWith('+91') || mobile?.startsWith('91')) && mobile?.length === 13 ? 11 : ""}
                            />
                            {mobileError && <div style={errorTextStyle}>Enter valid mobile number</div>}
                        </div>
                        <TextField
                            label="Email ID"
                            placeholder="Enter email ID"
                            value={email}
                            onChange={setEmail}
                            error={emailError}
                            errorMessage="Enter valid email"
                        />
                        <Dropdown
                            label="Classroom location"
                            placeholder="Select classroom location"
                            value={geofenceId}
                            options={locationOptions.length > 0 ? locationOptions : [{ id: 1, title: "Global" }]}
                            getLabel={o => o?.title || o?.geoFenceTitle || o?.name}
                            getValue={o => o?.id ?? o?.geoFenceId}
                            popoverKey="classroom-location"
                            openKey={openPopover}
                            onOpen={setOpenPopover}
                            onSelect={(val) => {
                                setGeofenceId(val);
                                const pool = locationOptions.length > 0 ? locationOptions : [{ id: 1, title: "Global" }];
                                const selected = pool.find(o => (o?.id ?? o?.geoFenceId) === val);
                                if (selected) {
                                    setGeofence(selected?.title || selected?.geoFenceTitle || selected?.name || "");
                                }
                                setOpenPopover(null);
                            }}
                        />
                    </div>
                </SectionCard>

                <SectionCard
                    title={courseEntries.length === 1 ? (
                        <>
                            Course enrollment{" "}
                            <span style={{ fontWeight: 400, fontSize: "16px", color: "#8A8A8A" }}>(optional)</span>
                        </>
                    ) : null}
                    isMobile={isMobile}
                >
                    {courseEntries.map((entry, index) => {
                        const entryError = errors.entries?.[index] || {};
                        const showNumberedHeading = courseEntries.length > 1;
                        const isLive = entry.type === 'live';
                        return (
                            <div key={index} ref={(node) => { entryRefs.current[index] = node; }} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                {/* {index > 0 && <div style={{ height: "1px", backgroundColor: "#E6E6E6" }} />} */}

                                {showNumberedHeading && (
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <span style={{ fontWeight: 500, fontSize: "20px", color: "#191919" }}>
                                            {`Course ${index + 1} enrollment`}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeCourseEntry(index)}
                                            style={{ border: "none", background: "transparent", padding: 0, fontFamily: "Roboto, sans-serif", fontWeight: 700, fontSize: "14px", letterSpacing: 0, color: "#FF414D", cursor: "pointer" }}
                                        >
                                            REMOVE
                                        </button>
                                    </div>
                                )}

                                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                    {availableCourseTypes.map(({ key, label, Icon, bg, color }) => {
                                        const selected = entry.type === key;
                                        return (
                                            <div
                                                key={key}
                                                // Click again to deselect.
                                                onClick={() => updateEntry(index, "type", selected ? null : key)}
                                                style={{
                                                    flex: "1 1 200px",
                                                    minWidth: 0,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "16px",
                                                    padding: "20px",
                                                    borderRadius: "16px",
                                                    cursor: "pointer",
                                                    boxSizing: "border-box",
                                                    backgroundColor: "#FFFFFF",
                                                    border: selected ? `1.5px solid #594099` : "",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                                }}
                                            >
                                                <div style={{
                                                    width: "44px", height: "44px", borderRadius: "12px", backgroundColor: bg,
                                                    flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                                                }}>
                                                    <Icon size={22} color={color} />
                                                </div>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "16px", fontWeight: 400, color: "#636363", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {label}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Fields appear only after a course type is selected. */}
                                {entry.type && (
                                <>
                                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                    <Dropdown
                                        label="Course"
                                        required
                                        placeholder="Select course"
                                        value={entry.course}
                                        defaultLabel={index === 0 ? (location.state?.courseName || "") : ""}
                                        // Hide courses already picked in another
                                        // enrollment block of the same course type
                                        // (keep this block's own selection).
                                        options={(courseOptionsMap[index] || []).filter((o) => {
                                            const optId = String(o?.id ?? o?._id);
                                            if (String(entry.course) === optId) return true;
                                            return !courseEntries.some((other, otherIdx) => (
                                                otherIdx !== index
                                                && other.type === entry.type
                                                && other.course !== "" && other.course != null
                                                && String(other.course) === optId
                                            ));
                                        })}
                                        getLabel={o => o?.name || o?.title || o?.courseName || o?.id}
                                        getValue={o => o?.id ?? o?._id}
                                        loading={!courseOptionsMap[index]}
                                        emptyText="No courses found"
                                        error={entryError.course}
                                        popoverKey={`course-${index}`}
                                        openKey={openPopover}
                                        onOpen={setOpenPopover}
                                        onSelect={(val) => updateEntry(index, "course", val)}
                                        onClear={() => updateEntry(index, "course", "", { keepOpen: true })}
                                        searchable
                                    />
                                    {isLive ? (
                                        <>
                                            <Dropdown
                                                label="Batch"
                                                required
                                                placeholder="Select batch"
                                                value={entry.batch}
                                                // The pre-filled batch name belongs to the course the page was
                                                // opened from — drop it once the course is changed.
                                                defaultLabel={index === 0 && String(entry.course) === String(location.state?.courseId ?? "")
                                                    ? ([location.state?.batchName, location.state?.sectionName].filter(Boolean).join(' - ') || "")
                                                    : ""}
                                                options={batchOptionsMap[index] || []}
                                                getLabel={o => (o?.std && o?.section) ? `${o.std} - ${o.section}` : (o?.name || o?.standardName || (o?.std || o?.section ? `${o?.std || ''} - ${o?.section || ''}`.trim().replace(/^- |-$/, '') : null) || o?.id)}
                                                getValue={o => o?.id ?? o?._id ?? o?.standard_id}
                                                loading={Boolean(entry.course) && !batchOptionsMap[index]}
                                                emptyText="No batches found"
                                                error={entryError.batch}
                                                popoverKey={`batch-${index}`}
                                                openKey={openPopover}
                                                onOpen={setOpenPopover}
                                                onSelect={(val) => updateEntry(index, "batch", val)}
                                                searchable
                                                disabled={!entry.course && !(index === 0 && location.state?.courseName)}
                                            />
                                            <TextField
                                                label="Roll number"
                                                placeholder={entry.rollLoading ? "Fetching roll number..." : "Enter roll number"}
                                                value={entry.rollNumber}
                                                onChange={(val) => updateEntry(index, "rollNumber", val)}
                                                disabled={entry.rollLoading || (!entry.course && !(index === 0 && location.state?.courseName))}
                                                error={isRollNumberTaken(entry)}
                                                errorMessage="Roll number already assigned"
                                            />
                                        </>
                                    ) : (
                                        <DateField
                                            value={entry.enrollmentDate}
                                            popoverKey={`date-${index}`}
                                            openKey={openPopover}
                                            onOpen={setOpenPopover}
                                            onSelect={(val) => updateEntry(index, "enrollmentDate", val)}
                                        />
                                    )}
                                </div>

                                {isLive && (
                                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                        <DateField
                                            value={entry.enrollmentDate}
                                            popoverKey={`date-${index}`}
                                            openKey={openPopover}
                                            onOpen={setOpenPopover}
                                            onSelect={(val) => updateEntry(index, "enrollmentDate", val)}
                                        />
                                        <div style={{ flex: "1 1 220px", minWidth: 0 }} />
                                        <div style={{ flex: "1 1 220px", minWidth: 0 }} />
                                    </div>
                                )}
                                </>
                                )}
                            </div>
                        );
                    })}

                    <div>
                        <button
                            type="button"
                            onClick={addCourseEntry}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                cursor: "pointer",
                                fontFamily: "Roboto, sans-serif",
                                fontWeight: 700,
                                fontSize: "14px",
                                letterSpacing: "0.4px",
                                color: COLORS.primary,
                            }}
                        >
                            <PlusIcon />
                            ADD ANOTHER COURSE
                        </button>
                    </div>
                </SectionCard>
            </div>

            <style>{`
                .add-student-option-row:hover { background-color: #F3F3F3; }
                .add-student-phone-wrapper.PhoneInput {
                    width: 100%;
                    height: 44px;
                    padding: 0 14px;
                    box-sizing: border-box;
                    border-radius: 12px;
                    border: none;
                    background-color: #F6F6F6;
                    display: flex;
                    align-items: center;
                }
                .add-student-phone-wrapper.PhoneInput.error {
                    border: 1px solid #FF414D;
                }
                .add-student-phone-wrapper .PhoneInputInput {
                    border: none;
                    outline: none;
                    box-shadow: none;
                    /* !important + higher specificity to beat the global
                       input[type='tel'] { background-color: white !important }
                       in userManagement/styles.css — scoped to this page only. */
                    background: transparent !important;
                    font-size: 15px;
                    color: #191919;
                    padding: 0;
                    -webkit-appearance: none;
                    appearance: none;
                }
                .add-student-phone-wrapper .PhoneInputCountry {
                    margin-right: 8px;
                }
            `}</style>

            <SavingModal
                visible={showConfirm}
                title="Confirm"
                message="Are you sure you want to add student?"
                confirmText="YES"
                cancelText="NO"
                onCancel={() => setShowConfirm(false)}
                onConfirm={handleConfirmSave}
                loading={saving}
                loadingText="SAVING..."
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    teachersGeofenceLocDataContainer: state?.attendanceNew?.teachersGeofenceLocDataContainer,
    marketSettingData: state?.studentMarketPlace?.marketSettingData,
});

const mapDispatchToProps = (dispatch) => ({
    addStudent: (params, callback) => dispatch(addStudent(params, callback)),
    enrollStudent: (userId, params, callback) => dispatch(enrollStudent(userId, params, callback)),
    getTeachersGeofenceLocations: (search, callback) => dispatch(getTeachersGeofenceLocations(search, callback)),
    getMarketSettingsData: (all, callback) => dispatch(getMarketSettingsData(all, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddStudent);
